import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Form, Switch, Tag } from 'antd'
import { NodeIndexOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'
import { API_ENDPOINTS } from '@/config/api'
import { GenericWorkflowDiagramEditor, StateVisual } from './GenericWorkflowDiagramEditor'

interface Role { id: number; nome: string }
interface WOWorkflowTransition {
  id: number; from_status: string; to_status: string
  required_role_id: number; description?: string; conditions_json?: string
  actions_json?: string; requires_reason: boolean; attivo: boolean; ordine: number
}
interface WOStatusType { id: number; nome: string; ordine: number; x?: number | null; y?: number | null }

const NODE_COLOR = '#1890ff'
const NODE_SPACING = 155
const NODE_Y = 200       // riga principale (y più alta per lasciare spazio alle frecce in su)
const NODE_Y_SUB = 380   // riga secondaria (stati con ordine decimale)

const getStateLabel = (state: string) =>
  state.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

const TRANSITIONS_URL = `${API_ENDPOINTS.SYSTEM_TABLES}/work-order-workflow-transitions`
const STATES_URL = `${API_ENDPOINTS.SYSTEM_TABLES}/work-order-status-types-list`

export const WorkOrderWorkflowDiagramEditor: React.FC = () => {
  const [transitions, setTransitions] = useState<WOWorkflowTransition[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [statusTypes, setStatusTypes] = useState<WOStatusType[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [tRes, rRes, sRes] = await Promise.all([
        axiosInstance.get(TRANSITIONS_URL),
        axiosInstance.get(`${API_ENDPOINTS.ROLES}?limit=100`),
        axiosInstance.get(STATES_URL),
      ])
      setTransitions(tRes.data.items ?? tRes.data)
      setRoles(rRes.data.items ?? rRes.data)
      setStatusTypes(sRes.data)
    } catch {
      // error handled by generic component
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Salva posizione x/y nel DB via PUT
  const handlePositionSave = useCallback(async (key: string, x: number, y: number) => {
    const statusType = statusTypes.find(s => s.nome === key)
    if (!statusType) return
    try {
      await axiosInstance.put(
        `${API_ENDPOINTS.SYSTEM_TABLES}/work-order-status-types/${statusType.id}`,
        { x, y }
      )
    } catch {
      // posizione non salvata, non critico
    }
  }, [statusTypes])

  // Calcola configurazione visuale e ordine dagli stati caricati dal DB.
  // Stati con ordine intero → riga principale (NODE_Y).
  // Stati con ordine decimale (es. 2.5) → riga secondaria (NODE_Y_SUB),
  //   posizionati sotto lo stato padre (floor dell'ordine).
  const { stateConfigVisual, stateOrder, stateKeys, stateSelectOptions } = useMemo(() => {
    const cfgVisual: Record<string, StateVisual> = {}
    const order: Record<string, number> = {}
    const keys: string[] = []

    const mainStates = statusTypes
      .filter(s => Number.isInteger(s.ordine))
      .sort((a, b) => a.ordine - b.ordine)
    const subStates = statusTypes
      .filter(s => !Number.isInteger(s.ordine))
      .sort((a, b) => a.ordine - b.ordine)

    // Mappa ordine intero → posizione x sulla riga principale
    const ordineToX: Record<number, number> = {}
    mainStates.forEach((s, idx) => {
      ordineToX[s.ordine] = 75 + idx * NODE_SPACING
    })

    // Riga principale: stateOrder = indice sequenziale (0,1,2...) per calcolo curve
    mainStates.forEach((s, idx) => {
      const defaultX = ordineToX[s.ordine]
      cfgVisual[s.nome] = {
        label: getStateLabel(s.nome),
        color: NODE_COLOR,
        icon: <NodeIndexOutlined />,
        x: s.x ?? defaultX,   // usa posizione salvata nel DB se presente
        y: s.y ?? NODE_Y,
      }
      order[s.nome] = idx
      keys.push(s.nome)
    })

    // Riga secondaria: x = x dello stato padre, stateOrder = 90 (eccezione)
    subStates.forEach(s => {
      const parentOrdine = Math.floor(s.ordine)
      const parentX = ordineToX[parentOrdine] ?? (75 + parentOrdine * NODE_SPACING)
      cfgVisual[s.nome] = {
        label: getStateLabel(s.nome),
        color: NODE_COLOR,
        icon: <NodeIndexOutlined />,
        x: s.x ?? parentX,    // usa posizione salvata nel DB se presente
        y: s.y ?? NODE_Y_SUB,
      }
      order[s.nome] = 90
      keys.push(s.nome)
    })

    const selectOpts = [...mainStates, ...subStates].map(s => ({
      value: s.nome,
      label: getStateLabel(s.nome),
      color: NODE_COLOR,
    }))

    return { stateConfigVisual: cfgVisual, stateOrder: order, stateKeys: keys, stateSelectOptions: selectOpts }
  }, [statusTypes])

  return (
    <GenericWorkflowDiagramEditor
      stateConfig={stateConfigVisual}
      stateKeys={stateKeys}
      stateOrder={stateOrder}
      transitions={transitions}
      roles={roles}
      loading={loading}
      nodeRadius={45}
      arrowheadId="arrowhead-wo"
      loadingTip="Caricamento workflow..."
      listTitle="Elenco Transizioni"
      editTitle="Modifica Transizione"
      createTitle="Nuova Transizione"
      getFromKey={t => t.from_status}
      getToKey={t => t.to_status}
      transitionsUrl={TRANSITIONS_URL}
      stateSelectOptions={stateSelectOptions}
      fromFieldName="from_status"
      toFieldName="to_status"
      defaultFormValues={{ attivo: true, requires_reason: false, ordine: 0 }}
      getAddFormValues={(from, to) => ({ from_status: from, to_status: to, attivo: true, requires_reason: false, ordine: 0 })}
      getEditFormValues={t => ({
        from_status: t.from_status, to_status: t.to_status,
        required_role_id: t.required_role_id, description: t.description,
        conditions_json: t.conditions_json, actions_json: t.actions_json,
        requires_reason: t.requires_reason, attivo: t.attivo, ordine: t.ordine,
      })}
      getDupFormValues={t => ({
        from_status: t.from_status, to_status: t.to_status,
        required_role_id: undefined,
        description: t.description,
        conditions_json: t.conditions_json, actions_json: t.actions_json,
        requires_reason: t.requires_reason, attivo: t.attivo, ordine: t.ordine + 1,
      })}
      extraFormFields={
        <Form.Item name="requires_reason" label="Richiede Motivazione" valuePropName="checked">
          <Switch checkedChildren="Sì" unCheckedChildren="No" />
        </Form.Item>
      }
      extraTransitionTag={t => t.requires_reason ? <Tag color="orange" style={{ marginLeft: 4 }}>Motivo</Tag> : null}
      onRefresh={fetchData}
      onPositionSave={handlePositionSave}
    />
  )
}
